/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, October 1st 2021, 7:47:51 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { GenericErrorState } from "lib/types";
import { APIErrorState, APIRequestState, STATUS_CODES } from "lib/api/types";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert, { AlertProps } from "@mui/material/Alert";

/**
 * Wrapper that returns a error alert when an error occurs
 *
 */

export interface APIErrorWrapperProps
  extends Partial<React.HTMLProps<HTMLDivElement>>,
    Omit<APIErrorState, "apiErrors">,
    Partial<Pick<APIRequestState, "httpResponseStatusCode">>,
    Pick<GenericErrorWrapperProps, "errorMessage"> {}

export const APIErrorWrapper = ({
  errorMessage,
  isAPIError,
  children,
  httpResponseStatusCode = null,
  ...props
}: APIErrorWrapperProps) => {
  const apiRequestErrorMessage = useHandleAPIErrorCode(httpResponseStatusCode);

  const defaultErrorMessage =
    errorMessage ?? (httpResponseStatusCode && `Error ${httpResponseStatusCode}: ${apiRequestErrorMessage}`);

  const isError = isAPIError && httpResponseStatusCode !== STATUS_CODES.PermissionDenied; // Let the Top leve API Error handler handle 403

  return (
    <>
      <GenericErrorWrapper
        isError={isError}
        errorMessage={
          defaultErrorMessage ??
          "An Error occurred while retrieving data. Sorry for the inconvenience, we are trying to fix the issue as soon as possible. Please try again later."
        }
      >
        {children}
      </GenericErrorWrapper>
    </>
  );
};

export interface GenericErrorWrapperProps extends Partial<React.HTMLProps<HTMLDivElement>>, GenericErrorState {}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export const GenericErrorWrapper = ({ errorMessage, isError, children, ...props }: GenericErrorWrapperProps) => {
  const [open, setOpen] = React.useState(false);

  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    setOpen(isError);
  }, [isError]);

  return isError ? (
    <SnackBarErrorMessage open={open} handleClose={handleClose} errorMessage={errorMessage} />
  ) : (
    <>{children}</>
  );
};

const SnackBarErrorMessage = ({ open, handleClose, errorMessage }) => (
  <Snackbar open={open} onClose={handleClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
    <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
      {errorMessage ?? "An Error occurred."}
    </Alert>
  </Snackbar>
);

export const useHandleAPIErrorCode = (httpResponseStatusCode: number) => {
  // TODO: Automatically redirect to login page if we have confirmed a user session has expired.
  if ([STATUS_CODES.ClientError].includes(httpResponseStatusCode)) return "Invalid Request.";

  if ([STATUS_CODES.NotFound].includes(httpResponseStatusCode)) return "The requested item could not be found.";

  if ([STATUS_CODES.MethodNotAllowed].includes(httpResponseStatusCode)) return "The request method is not allowed.";

  if ([STATUS_CODES.Unauthorized, STATUS_CODES.PermissionDenied].includes(httpResponseStatusCode))
    return "You do not have permission to view this item. Kindly ensure you are logged in. Your login session may also have expired.";

  if (httpResponseStatusCode >= STATUS_CODES.ClientError && httpResponseStatusCode <= STATUS_CODES.ClientClosedRequest)
    return "A Client Error occured";

  if (
    httpResponseStatusCode >= STATUS_CODES.ServerError &&
    httpResponseStatusCode <= STATUS_CODES.NetworkConnectTimeout
  )
    return "A Server Error occurred. Our sincere apologies. We are working on the issue.";

  if (httpResponseStatusCode === STATUS_CODES.UnreachableServerErrorCode)
    return "You are not able to reach our servers. Kindly check your internet connectivity";

  return null;
};
